import { Alert, Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import "./styles.css";
import { useSelector } from "react-redux";
import SimpleSelect from "../selects/simpleSelect";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormTextField from "../textField/formTextField";
import FormCalendar from "../calendar/formCalendar";
import LaddaButton from "../buttons/laddaButton";
import { isRoleUtil } from "../../utils/AuthorizedUtils/360Report";
import { canEditByRol, canEditStrategyByRol, getRolbyProcessId } from "../../screens/dofa/authorizeFunctions";
import { parseDateAsLocalISO, toYMD } from "../../utils/dateUtils";
import { callActionApiAsync, getProcessLeaders, postSaveStrategiesDofaBanner, getStrategiesDofaBanner } from "../../services/apiService";
import VerificationModal from "../modals/verificationModal";

const validationSchema = yup.object({
  elaborated: yup.number().typeError("Selecciona un responsable").required("Selecciona un responsable"),
  charge: yup.string().trim().required("Ingresa el cargo").max(255, "Máximo 255 caracteres"),
  productionDate: yup.date().nullable().required("Selecciona la fecha de elaboración")
});

const MonthlyBanner = ({ rowData, year, month }) => {
  const userData = useSelector((state) => state.userData.userData);
  const processId = rowData.ProcessId;
  const [bannerData, setBannerData] = useState(null);
  const [activeLeaders, setActiveLeaders] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [leadersLoaded, setLeadersLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [openVerification, setOpenVerification] = useState(false);
  const { control, handleSubmit, reset, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { elaborated: "", charge: "", productionDate: null }
  });

  const isEditor = isRoleUtil("DOFA_EDITOR_BANNER_MANAGER", userData.roleList);
  const now = new Date();
  const currentPeriod = now.getFullYear() * 12 + now.getMonth() + 1;
  const selectedPeriod = Number(year) * 12 + Number(month);
  const canEdit = selectedPeriod <= currentPeriod && (isEditor ||
    (selectedPeriod === currentPeriod && (canEditByRol(userData.roleList, processId) ||
      canEditStrategyByRol(userData.roleList, processId))));
  const isAvailableToAdd = loaded && leadersLoaded && !saving && canEdit;

  // Las opciones activas y el valor guardado se combinan al renderizar; el orden
  // de llegada de las dos consultas no puede borrar al responsable historico.
  const leadersSelect = useMemo(() => {
    const options = activeLeaders.map((leader) => ({ ...leader }));
    if (bannerData?.user_id != null) {
      const index = options.findIndex((leader) => String(leader.value) === String(bannerData.user_id));
      const saved = {
        value: bannerData.user_id,
        name: bannerData.user_name || `Usuario ${bannerData.user_id}`,
        disabled: Number(bannerData.user_is_active) !== 1 || index < 0
      };
      if (index < 0) options.push(saved);
      else options[index] = saved;
    }
    return options;
  }, [activeLeaders, bannerData]);

  useEffect(() => {
    let active = true;
    callActionApiAsync(
      () => getStrategiesDofaBanner(processId, year, month),
      (data) => {
        if (!active) return;
        setBannerData(data);
        reset({
          elaborated: data?.user_id ?? "",
          charge: data?.charge || "",
          productionDate: parseDateAsLocalISO(data?.elaborated_date)
        });
        setLoaded(true);
      },
      () => { if (active) setMessage({ severity: "error", text: "No se pudo cargar el responsable del periodo" }); }
    );
    callActionApiAsync(
      () => getProcessLeaders(getRolbyProcessId(processId)),
      (data) => {
        if (!active) return;
        setActiveLeaders(Array.isArray(data) ? data : []);
        setLeadersLoaded(true);
      },
      () => { if (active) setMessage({ severity: "error", text: "No se pudo cargar la lista de líderes activos" }); }
    );
    return () => { active = false; };
  }, [processId, year, month, reset]);

  const handleSaveBanner = async () => {
    setOpenVerification(false);
    if (!isAvailableToAdd) return;
    setSaving(true);
    setMessage(null);
    const values = getValues();
    await callActionApiAsync(
      () => postSaveStrategiesDofaBanner({
        processId, year: Number(year), month: Number(month), userId: userData.id,
        elaborated: values.elaborated, charge: values.charge,
        productionDate: toYMD(values.productionDate)
      }),
      async () => {
        await callActionApiAsync(
          () => getStrategiesDofaBanner(processId, year, month),
          (data) => {
            setBannerData(data);
            setMessage({ severity: "success", text: "Responsable del periodo guardado" });
          },
          () => setMessage({ severity: "error", text: "Se guardó el banner, pero no se pudo recargar" })
        );
      },
      (error) => setMessage({ severity: "error", text: typeof error === "string" ? error : "No se pudo guardar el banner" })
    );
    setSaving(false);
  };

  return (
    <div className="managmentBanner-mini bm-30">
      {message && <Alert severity={message.severity}>{message.text}</Alert>}
      <br/>
      {loaded && !bannerData && <p>Sin responsable registrado para este periodo.</p>}
      <form onSubmit={handleSubmit(() => setOpenVerification(true))}>
        <Grid container rowSpacing={2} sx={{ alignItems: "center" }}>
          <Grid item xs={2}>
            <p className="detail-label m-0">Elaborado:</p>
          </Grid>
          <Grid item xs={12} md={10}>
            <SimpleSelect name="elaborated" control={control} label="" options={leadersSelect}
              error={errors.elaborated} width="95%" disabled={!isAvailableToAdd} margin="0" />
          </Grid>
          <Grid item xs={12} md={2}>
            <p className="detail-label m-0">Cargo:</p>
          </Grid>
          <Grid item xs={12} md={10}>
            <FormTextField name="charge" control={control} error={errors.charge}
              width="95%" rows={1} disabled={!isAvailableToAdd} margin="0" />
          </Grid>
          <Grid item xs={12} md={2}>
            <p className="detail-label m-0">Fecha elaboración:</p>
          </Grid>
          <Grid item xs={12} md={10}>
            <FormCalendar name="productionDate" control={control} label="" defaultValue={null}
              formLabel="" error={errors.productionDate} width="95%" disabled={!isAvailableToAdd}
              maxDate={new Date(Number(year), Number(month), 0, 12)} margin="0" />
          </Grid>
        </Grid>
        {canEdit && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
            <LaddaButton width="200px" variant="contained" disabled={!isAvailableToAdd}
              onClick={handleSubmit(() => setOpenVerification(true))}>
              <div className="HContainer align-center"><p className="button_text">Guardar</p></div>
            </LaddaButton>
          </div>
        )}
      </form>
      <VerificationModal open={openVerification} handleClose={() => setOpenVerification(false)}
        alertText={<><b>¿Estás seguro de guardar los cambios?</b><br />
          Se actualizará el responsable de DOFA y Estrategias únicamente para {String(month).padStart(2, "0")}/{year}.
        </>}
        onConfirm={handleSaveBanner} />
    </div>
  );
};

const StrategiesDofaBanner = (props) => (
  <MonthlyBanner key={`${props.rowData.ProcessId}-${props.year}-${props.month}`} {...props} />
);

export default StrategiesDofaBanner;
